import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import config from '../config';
import { fetchWithCSRF } from '../utils/csrfProtection';

const POLL_INTERVAL_MS = 30000; // 30-second background poll

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, getToken } = useAuth();
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Queue of toasts to show: [{ id, title, message, type }]
  const [toastQueue, setToastQueue] = useState([]);

  // Keep a ref of known IDs so polling can detect genuinely new ones
  const knownIds = useRef(new Set());

  // Recalculate unread count whenever notifications change
  useEffect(() => {
    const count = notifications.filter(n => !n.read).length;
    setUnreadCount(count);
  }, [notifications]);

  // Helper to build auth headers — safe even when getToken() throws (dev mock auth)
  const authHeaders = useCallback(async () => {
    try {
      const token = await getToken();
      if (token) return { Authorization: `Bearer ${token}` };
    } catch {
      // In dev mock-auth mode getToken() throws — the backend falls back to mock user
    }
    return {};
  }, [getToken]);

  // Push a toast into the queue
  const pushToast = useCallback((notification) => {
    setToastQueue(prev => [
      ...prev,
      {
        toastId: `toast-${notification.id}-${Date.now()}`,
        id: notification.id,
        title: notification.title,
        message: notification.message,
        type: notification.type || 'info'
      }
    ]);
  }, []);

  // Dismiss one toast from the queue by toastId.
  // Pass { markRead: true } to also mark the notification as read (manual close).
  // Pass { markRead: false } (default) to just remove it and leave it unread (auto-timeout).
  const dismissToast = useCallback((toastId, { markRead = false } = {}) => {
    setToastQueue(prev => {
      const toast = prev.find(t => t.toastId === toastId);
      if (markRead && toast?.id) {
        setNotifications(ns =>
          ns.map(n => n.id === toast.id ? { ...n, read: true } : n)
        );
        authHeaders().then(headers =>
          fetchWithCSRF(`${config.apiUrl}/api/notifications/${toast.id}/read`, {
            method: 'PATCH', headers
          }).catch(err => console.error('Error marking notification as read on dismiss:', err))
        );
      }
      return prev.filter(t => t.toastId !== toastId);
    });
  }, [authHeaders]);

  // Core fetch — returns new notifications (ones not yet in knownIds)
  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = await authHeaders();
      const response = await fetch(`${config.apiUrl}/api/notifications?limit=50`, { headers });
      if (!response.ok) throw new Error('Failed to fetch notifications');
      const data = await response.json();
      if (!data.success) return;

      const incoming = data.data || [];

      // Detect brand-new notifications (not in our known set)
      const newOnes = incoming.filter(n => !knownIds.current.has(n.id));

      // Update known IDs
      incoming.forEach(n => knownIds.current.add(n.id));

      // Merge new notifications into state (avoid duplicates)
      if (newOnes.length > 0) {
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const toAdd = newOnes.filter(n => !existingIds.has(n.id));
          return toAdd.length > 0 ? [...toAdd, ...prev] : prev;
        });

        // Toast only unread new ones
        newOnes.filter(n => !n.read).forEach(n => pushToast(n));
      } else if (!silent) {
        // Initial load — just set everything, no toasts
        setNotifications(incoming);
        setUnreadCount(data.unread || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authHeaders, pushToast]);

  // Exposed manual refresh (not silent — shows spinner)
  const loadNotifications = useCallback(() => fetchNotifications(false), [fetchNotifications]);

  // Initial load on mount / user change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchNotifications(false); }, [user?.id]);

  // Background polling every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => fetchNotifications(true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchNotifications]);

  // ---- Socket.IO real-time push ----
  const joinedRoomId = useRef(null);

  const joinRoom = useCallback((userId) => {
    if (!socket || !userId || joinedRoomId.current === userId) return;
    socket.emit('join_user_room', userId);
    joinedRoomId.current = userId;
  }, [socket]);

  useEffect(() => {
    if (user?.id) joinRoom(user.id);
  }, [user?.id, joinRoom]);

  useEffect(() => {
    if (!user?.id && notifications.length > 0 && notifications[0].userId) {
      joinRoom(notifications[0].userId);
    }
  }, [notifications, user?.id, joinRoom]);

  useEffect(() => {
    if (!socket) { joinedRoomId.current = null; }
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    const handleNotification = (notification) => {
      // Guard against duplicates from the Socket.IO push
      if (knownIds.current.has(notification.id)) return;
      knownIds.current.add(notification.id);
      setNotifications(prev => [{ ...notification, read: false }, ...prev]);
      pushToast({ ...notification, read: false });
    };
    socket.on('notification', handleNotification);
    return () => socket.off('notification', handleNotification);
  }, [socket, pushToast]);

  // ---- Mutating actions ----

  const markAsRead = useCallback(async (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    try {
      const headers = await authHeaders();
      await fetchWithCSRF(`${config.apiUrl}/api/notifications/${notificationId}/read`, {
        method: 'PATCH', headers
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [authHeaders]);

  const markAsUnread = useCallback(async (notificationId) => {
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: false } : n)
    );
    try {
      const headers = await authHeaders();
      await fetchWithCSRF(`${config.apiUrl}/api/notifications/${notificationId}/unread`, {
        method: 'PATCH', headers
      });
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  }, [authHeaders]);

  const markAllAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    try {
      const headers = await authHeaders();
      await fetchWithCSRF(`${config.apiUrl}/api/notifications/mark-all-read`, {
        method: 'PATCH', headers
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [authHeaders]);

  const deleteNotification = useCallback(async (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    knownIds.current.delete(notificationId);
    try {
      const headers = await authHeaders();
      await fetchWithCSRF(`${config.apiUrl}/api/notifications/${notificationId}`, {
        method: 'DELETE', headers
      });
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [authHeaders]);

  const clearAllNotifications = useCallback(async () => {
    setNotifications([]);
    knownIds.current.clear();
    try {
      const headers = await authHeaders();
      await fetchWithCSRF(`${config.apiUrl}/api/notifications`, {
        method: 'DELETE', headers
      });
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  }, [authHeaders]);

  const addNotification = useCallback((notification) => {
    const n = {
      id: Date.now(),
      createdAt: new Date().toISOString(),
      read: false,
      type: 'info',
      category: 'system',
      ...notification
    };
    knownIds.current.add(n.id);
    setNotifications(prev => [n, ...prev]);
    pushToast(n);
  }, [pushToast]);

  const value = {
    notifications,
    unreadCount,
    loading,
    toastQueue,
    dismissToast,
    loadNotifications,
    addNotification,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
